import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import type {CourseAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {
  ASSIGNABLE_USER_STATES,
  OPEN_COURSE_ASSIGNMENT_STATES,
} from 'src/app/training/courseAssignmentFilters';
import {
  CourseAssignmentRowPills,
  courseAssignmentRowSubText,
} from 'src/app/training/courseAssignmentPillConfig';
import {TrainingIcon} from 'src/app/training/coursePills';
import {Badge} from 'src/design/atoms/Badge';
import {Box} from 'src/design/atoms/Box';
import {Flex} from 'src/design/atoms/Flex';
import {NavAnchor} from 'src/design/atoms/NavAnchor';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {AllClearState} from 'src/design/molecules/AllClearState';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {useGetCourseAssignmentListQuery} from 'src/redux/queries/courseAssignment';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {CoursePreviewDrawer} from '../CoursePreviewDrawer/CoursePreviewDrawer';

const MAX_ITEMS_PER_PAGE = 5;

export const OpenCoursesView: React.FC = () => {
  const {currentPage, handlePageChange, start, length} = useListState({
    identifier: 'dashboard-open-courses',
    pageSize: MAX_ITEMS_PER_PAGE,
  });

  const {data, isLoading, error} = useGetCourseAssignmentListQuery({
    start,
    length,
    sortColumn: 'dueAt',
    sortDirection: 'desc',
    states: OPEN_COURSE_ASSIGNMENT_STATES,
    activeStates: [true],
    viewCurrentUserOnly: true,
    userActiveStates: ASSIGNABLE_USER_STATES,
  });

  const courses = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  const [previewItem, setPreviewItem] = useState<
    CourseAssignmentSimpleResponse | undefined
  >();
  const [previewOpened, setPreviewOpened] = useState(false);

  const handleOpenPreview = (
    _id: number | string,
    item: CourseAssignmentSimpleResponse
  ) => {
    setPreviewItem(item);
    setPreviewOpened(true);
  };

  const sectionHeaderCount = totalRecords > 0 && (
    <Badge size="lg" variant="light" color="gray" circle>
      {totalRecords}
    </Badge>
  );

  return (
    <>
      <SectionCard
        title="My Training"
        titleAdornment={sectionHeaderCount}
        rightSection={
          // Open Tasks' search input height, so the paired headers align.
          <Flex mih={36} align="center">
            <NavAnchor
              text="View all"
              component={Link}
              to={PathUtility.TrainingDashboard('user')}
            />
          </Flex>
        }>
        <Box px="sm" py="xs">
          {error ? (
            <ErrorAlert mt={0} message={parseErrorMessage(error)} />
          ) : totalRecords === 0 && !isLoading ? (
            <AllClearState />
          ) : (
            <DataList
              rowIcon={TrainingIcon}
              isLoading={isLoading}
              data={courses}
              currentPage={currentPage}
              totalRecords={totalRecords}
              recordsPerPage={MAX_ITEMS_PER_PAGE}
              onRowClick={handleOpenPreview}
              onPageChange={handlePageChange}
              onGetPillMapping={CourseAssignmentRowPills}
              onRowSubTextRender={courseAssignmentRowSubText}
              onRowTextRender={(item: CourseAssignmentSimpleResponse) =>
                item.courseDefinition?.name ?? 'Unknown'
              }
              hasSearchTerm={false}
              hasCreationLogic={false}
              paginatorAtBottom
              meta={undefined}
            />
          )}
        </Box>
      </SectionCard>

      <CoursePreviewDrawer
        item={previewItem}
        opened={previewOpened}
        onClose={() => setPreviewOpened(false)}
      />
    </>
  );
};
